import { useCallback, useEffect, useState } from 'react';

/**
 * localStorage key holding the md+ sidebar's collapsed/expanded preference.
 *
 * Deliberately NOT namespaced per user or per organization: this is a display
 * preference for the person sitting at the browser, not data about a tenant, so
 * there is nothing here a second user on the same machine could learn.
 */
export const SIDEBAR_COLLAPSED_KEY = 'shell.sidebarCollapsed';

/** Keyboard shortcut that toggles the rail, rendered the way the platform writes it. */
export const SIDEBAR_SHORTCUT_LABEL =
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/.test(navigator.userAgent)
    ? '⌘B'
    : 'Ctrl+B';

/**
 * Read the saved preference, defaulting to the expanded (labelled) sidebar.
 *
 * Icon-only navigation is measurably harder to learn than labelled navigation, so
 * the rail is something the user opts INTO — never the state they are dropped in.
 */
function readCollapsed(): boolean {
  try {
    return window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';
  } catch {
    return false; // unavailable / blocked storage — behave as if nothing was saved
  }
}

/** True when the keystroke landed in a field where `b` means the letter b. */
function isTypingTarget(target: EventTarget | null): boolean {
  return (
    target instanceof Element &&
    target.closest('input, textarea, select, [contenteditable="true"]') !== null
  );
}

/**
 * The md+ sidebar's collapsed/expanded state, persisted across reloads and
 * toggleable with `⌘B` / `Ctrl+B`.
 *
 * The preference survives a reload because a navigation chrome that silently
 * re-expands is worse than one that never collapsed: the user re-does the same
 * click every session and stops trusting the control.
 */
export function useSidebarCollapsed(): [boolean, () => void] {
  const [collapsed, setCollapsed] = useState(readCollapsed);

  const toggle = useCallback(() => {
    setCollapsed((current) => {
      const next = !current;
      try {
        window.localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next));
      } catch {
        /* ignore quota / blocked storage — the choice just won't outlive the tab */
      }
      return next;
    });
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      // Bare modifier + B only: `⌥⌘B` / `Ctrl+Shift+B` belong to whoever else wants them.
      if (!(event.metaKey || event.ctrlKey) || event.altKey || event.shiftKey) {
        return;
      }
      if (event.key.toLowerCase() !== 'b' || isTypingTarget(event.target)) {
        return;
      }
      event.preventDefault();
      toggle();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [toggle]);

  return [collapsed, toggle];
}
